import { useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import { PendingApprovalScreen } from './auth/PendingApprovalScreen';
import { SignInScreen } from './auth/SignInScreen';
import { EmptyState } from './components/ui/EmptyState';
import { Spinner } from './components/ui/Spinner';
import { ThemeProvider } from './lib/ThemeContext';
import { migrateLegacySessionScenarios } from './lib/scenarioStore';
import { router } from './routes';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

export function App(): React.JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      {/* Outside the auth gate: the sign-in screen and the error/loading
          states should honour the theme too, not just the signed-in app. */}
      <ThemeProvider>
        <AuthProvider>
          <Gate />
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

/** Blocks the whole app behind authentication before anything renders. */
function Gate(): React.JSX.Element {
  const { status, user, errorMessage, signOut } = useAuth();
  const accountStatus = user?.status;

  // One-time carry-over from the sessionStorage-only version of this app —
  // see scenarioStore.ts's migrateLegacySessionScenarios for why this is safe
  // to fire and forget rather than gating the UI on it: it is a best-effort
  // background step, not something a sign-in should ever wait on.
  //
  // Gated on an *active* account as well as an authenticated one: a pending
  // account is refused by POST /scenarios, so running it then would burn the
  // one-shot marker on a set of requests that all 403, losing the very work
  // the carry-over exists to save.
  useEffect(() => {
    if (status === 'authenticated' && accountStatus === 'active') {
      void migrateLegacySessionScenarios();
    }
  }, [status, accountStatus]);

  if (status === 'loading') {
    return (
      <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center' }}>
        <Spinner size={28} />
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return <SignInScreen />;
  }

  if (status === 'error') {
    return (
      <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center' }}>
        <EmptyState title="Something went wrong" body={errorMessage ?? 'Could not sign you in.'} />
      </div>
    );
  }

  // Authenticated, but an admin has not granted access. Rendered instead of
  // the router rather than inside it: every API route except /auth/session
  // refuses this account, so mounting the app would show a dashboard of failed
  // requests. See apps/api/src/plugins/auth.ts.
  if (user && user.status !== 'active') {
    return <PendingApprovalScreen status={user.status} email={user.upn} onSignOut={signOut} />;
  }

  return <RouterProvider router={router} />;
}
